import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database.types';
import type { EnrichedNotification, NotificationFilters } from '../types';

export class NotificationError extends Error {
  constructor(
    public code: 'UNAUTHENTICATED' | 'QUERY_FAILED' | 'MUTATION_FAILED' | 'INVALID_INPUT',
    message: string
  ) {
    super(message);
    this.name = 'NotificationError';
  }
}

/**
 * Formats an ISO date string into a concise relative human-readable format.
 */
export function formatRelativeTime(isoString: string): string {
  try {
    const diffMs = Date.now() - new Date(isoString).getTime();
    if (diffMs < 0) return 'just now';

    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'just now';

    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;

    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return new Date(isoString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'recently';
  }
}

/**
 * Fetches notifications for a given recipient user.
 */
export async function fetchNotifications(
  client: SupabaseClient<Database>,
  userId: string,
  options?: NotificationFilters
): Promise<EnrichedNotification[]> {
  if (!client) {
    throw new NotificationError('UNAUTHENTICATED', 'Supabase client is required.');
  }
  if (!userId || !userId.trim()) {
    return [];
  }

  const cleanUserId = userId.trim();
  const limit = options?.limit || 30;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query = (client.from('notifications') as any)
    .select(`
      id,
      recipient_id,
      actor_id,
      event_type,
      resource_id,
      resource_type,
      message,
      is_read,
      created_at,
      actor:profiles!actor_id (
        id,
        username,
        display_name,
        avatar_path
      )
    `)
    .eq('recipient_id', cleanUserId);

  if (options?.unreadOnly) {
    query = query.eq('is_read', false);
  }

  query = query.order('created_at', { ascending: false }).limit(limit);

  const { data, error } = await query;

  if (error) {
    throw new NotificationError('QUERY_FAILED', `Failed to load notifications: ${error.message}`);
  }

  if (!data || !Array.isArray(data)) {
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return data.map((item: any) => {
    let avatarUrl: string | null = null;
    if (item.actor?.avatar_path) {
      const { data: publicUrlData } = client.storage
        .from('avatars')
        .getPublicUrl(item.actor.avatar_path);
      avatarUrl = publicUrlData?.publicUrl || null;
    }

    return {
      id: item.id,
      recipient_id: item.recipient_id,
      actor_id: item.actor_id,
      event_type: item.event_type,
      resource_id: item.resource_id,
      resource_type: item.resource_type,
      message: item.message,
      is_read: item.is_read,
      created_at: item.created_at,
      actor: item.actor
        ? {
            id: item.actor.id,
            username: item.actor.username,
            displayName: item.actor.display_name,
            avatarUrl,
          }
        : null,
    };
  });
}

/**
 * Fetches the count of unread notifications for a user.
 */
export async function fetchUnreadCount(
  client: SupabaseClient<Database>,
  userId: string
): Promise<number> {
  if (!client || !userId || !userId.trim()) {
    return 0;
  }

  const cleanUserId = userId.trim();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { count, error } = await (client.from('notifications') as any)
    .select('id', { count: 'exact', head: true })
    .eq('recipient_id', cleanUserId)
    .eq('is_read', false);

  if (error) {
    // Non-blocking fallback
    return 0;
  }

  return count || 0;
}

/**
 * Marks a specific notification as read.
 */
export async function markNotificationAsRead(
  client: SupabaseClient<Database>,
  notificationId: string,
  userId: string
): Promise<{ success: true }> {
  if (!client) {
    throw new NotificationError('UNAUTHENTICATED', 'Supabase client is required.');
  }
  if (!notificationId || !notificationId.trim()) {
    throw new NotificationError('INVALID_INPUT', 'Notification ID is required.');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.from('notifications') as any)
    .update({ is_read: true })
    .eq('id', notificationId.trim())
    .eq('recipient_id', userId.trim());

  if (error) {
    throw new NotificationError(
      'MUTATION_FAILED',
      `Failed to mark notification as read: ${error.message}`
    );
  }

  return { success: true };
}

/**
 * Marks all unread notifications as read for the given user.
 */
export async function markAllNotificationsAsRead(
  client: SupabaseClient<Database>,
  userId: string
): Promise<{ success: true }> {
  if (!client) {
    throw new NotificationError('UNAUTHENTICATED', 'Supabase client is required.');
  }
  if (!userId || !userId.trim()) {
    throw new NotificationError('INVALID_INPUT', 'User ID is required.');
  }

  const cleanUserId = userId.trim();

  // 1. First attempt atomic RPC
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: rpcError } = await (client as any).rpc('mark_all_notifications_read', {
      p_user_id: cleanUserId,
    });
    if (!rpcError) {
      return { success: true };
    }
  } catch {
    // Fall back to direct PostgREST update
  }

  // 2. Fall back to standard update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.from('notifications') as any)
    .update({ is_read: true })
    .eq('recipient_id', cleanUserId)
    .eq('is_read', false);

  if (error) {
    throw new NotificationError(
      'MUTATION_FAILED',
      `Failed to mark all as read: ${error.message}`
    );
  }

  return { success: true };
}
